import moment from "moment";

import { IndividualRun } from "@/types/metadata";
import { RoleI } from "@/components/Playground/types";
import { Variable } from "@/components/InputVariableConfigurator/types";
import {
  ChatTemplate,
  CompletionTemplate,
  PromptBlueprint,
  Template,
} from "@/types/prompt-blueprint";
import { getResponseMessage } from "@/utils/utils";

/** Get the response message from a completed run (works for both streaming and non-streaming). */
const getFinalMessage = (run: IndividualRun) => {
  const template = run.request?.prompt_version?.prompt_template as Template | undefined;
  if (!template) {
    return undefined;
  }

  if ("messages" in template && (template as ChatTemplate).messages?.length) {
    return getResponseMessage(template as ChatTemplate);
  }

  if (template.type === "completion") {
    const completionTemplate = template as CompletionTemplate;
    if (completionTemplate.content?.length) {
      const outputOnly = completionTemplate.content.filter(c => c.type === "output_media");
      if (outputOnly.length) {
        return { role: "assistant" as const, content: outputOnly };
      }

      const responseContent = completionTemplate.content.slice(-1);
      return { role: "assistant" as const, content: responseContent };
    }
  }

  return undefined;
};

const getFilteredValue = (value: string) =>
  value
    .replace(/[\n\s]+/g, " ")
    .replace(/\s*\[\s*/g, "[")
    .replace(/\s*\]\s*/g, "]")
    .replace(/,(\s*[}\]])/g, "$1");

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const parseTypeOptimistically = (value: any) => {
  try {
    const filteredValue = getFilteredValue(value);
    const jsonValue = JSON.parse(filteredValue);
    return jsonValue;
  } catch {
    return value;
  }
};

export const getVariableValueIsFilled = (value: unknown): boolean => {
  if (value === null || value === undefined) {
    return false;
  }

  if (typeof value === "string") {
    return value.trim().length > 0;
  }

  if (typeof value === "object") {
    return Object.keys(value).length > 0;
  }

  return true;
};

const formatBlueprintForAPI = (
  blueprint: PromptBlueprint,
  inputVariableSets?: { id: string; name?: string; variables: Variable[] }[],
) => {
  if (blueprint.metadata?.model?.model_config_id) {
    blueprint.metadata.model.model_config_id = undefined;
  }

  // Convert input variable sets to API format: Record<string, unknown>[][]
  const formattedInputVariableSets = inputVariableSets
    ? inputVariableSets.map(set =>
        set.variables.map(variable => ({
          [variable.key]: variable.value,
        })),
      )
    : undefined;

  // Extract input variable set names
  const inputVariableSetNames = inputVariableSets
    ?.map(set => set.name)
    .filter((name): name is string => !!name);

  return {
    ...blueprint,
    metadata: {
      ...blueprint.metadata,
    },
    ...(formattedInputVariableSets && {
      input_variable_sets: formattedInputVariableSets,
    }),
    ...(inputVariableSetNames &&
      inputVariableSetNames.length > 0 && {
        input_variable_set_names: inputVariableSetNames,
      }),
  };
};

export const getRoleDisplayName = (role: RoleI) =>
  role ? `${role.charAt(0).toUpperCase()}${role.slice(1).toLowerCase()}` : "";

export const getLatencyMs = (dateStartAt: string, dateEndAt: string) =>
  moment(dateEndAt).diff(moment(dateStartAt), "ms") ?? 0;

export const formatLatency = (duration: moment.Duration) => {
  if (duration.asSeconds() >= 60) {
    return `${Math.floor(duration.asMinutes())}m ${duration.seconds()}s`;
  } else if (duration.asMilliseconds() >= 1000) {
    return `${duration.seconds()}s`;
  }
  return `${Math.floor(duration.asMilliseconds())}ms`;
};

/**
 * Build a composite message ID.
 *
 * - User messages:     `{request_id}_user`        (no index)
 * - Response messages:  `{request_id}_{role}_{index}`  (e.g. `42_assistant_0`)
 *
 * The backend's `parse_message_id` expects exactly these two formats.
 * Omitting the index for response messages previously produced IDs like
 * `42_assistant` which don't match any backend pattern, causing "Invalid
 * message ID" errors on edit/delete.
 */
export const getMessageId = (individual_run_request_id: number, role: RoleI, index?: number) => {
  if (role === "user" || index === undefined) {
    return `${individual_run_request_id}_${role}`;
  }
  return `${individual_run_request_id}_${role}_${index}`;
};

/**
 * Deconstruct a composite message ID back into its parts.
 *
 * Handles both `{id}_user` and `{id}_{role}_{index}` formats.
 */
export const deconstructMessageId = (messageId: string) => {
  const parts = messageId.split("_");
  return {
    individual_run_request_id: parseInt(parts[0], 10),
    role: parts[1] as RoleI,
    index: parts.length >= 3 ? parseInt(parts[2], 10) : undefined,
  };
};

/**
 * When replaying one tool response from a parallel tool-call turn, return every
 * sibling tool response that belongs to the same submitted turn.
 *
 * Parallel tool responses are stored/submitted together. Replaying only the
 * clicked sibling would omit the others from the LLM request history.
 */
export const getToolReplayGroup = <
  T extends {
    role: string;
    individual_run_request_id?: number | null;
  },
>(
  messages: T[],
  targetIndex: number,
): { startIndex: number; messages: T[] } => {
  const target = messages[targetIndex];
  if (!target || target.role !== "tool") {
    return { startIndex: targetIndex, messages: target ? [target] : [] };
  }

  const requestId = target.individual_run_request_id;
  let start = targetIndex;
  let end = targetIndex;

  const isSibling = (message: T) => {
    if (message.role !== "tool") {
      return false;
    }
    if (requestId === null || requestId === undefined) {
      return true;
    }
    return message.individual_run_request_id === requestId;
  };

  while (start > 0 && isSibling(messages[start - 1])) {
    start -= 1;
  }
  while (end + 1 < messages.length && isSibling(messages[end + 1])) {
    end += 1;
  }

  return {
    startIndex: start,
    messages: messages.slice(start, end + 1),
  };
};

export { getFinalMessage, parseTypeOptimistically, getFilteredValue, formatBlueprintForAPI };
